import cn from 'classnames';
import { CSSProperties, ReactNode, forwardRef, ForwardedRef } from 'react';

export interface ButtonProps {
  buttonType?: 'button' | 'reset' | 'submit';
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  onClick?: () => void;
  type?: 'primary' | 'secondary' | 'white';
  style?: CSSProperties;
}

function Loader() {
  return (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonType = 'button',
      children,
      className,
      disabled = false,
      loading = false,
      loadingText = 'Processing...',
      onClick,
      type = 'white',
      style,
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center px-4 py-2 border shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
          {
            'border-transparent text-white bg-rum-600 hover:bg-rum-700 focus:ring-rum-500':
              type === 'primary',
            'border-gray-300  text-gray-700 bg-white hover:bg-gray-50 focus:ring-rum-500':
              type === 'secondary',
            'opacity-70': disabled,
          },
          className,
        )}
        disabled={disabled}
        onClick={onClick}
        style={style}
        type={buttonType}
        ref={ref}
      >
        {loading ? (
          <>
            <Loader />
            <span>{loadingText}</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
